import { baseURL } from 'app/client';
import { SignalRConfig, useSignalR } from 'core/hooks/useSignalR';
import { useEffect, useCallback, useState } from 'react';

export interface SignalRNotificationsConfig<T> {
  /** Route SignalR (ex: '/signalr/document-notifications') */
  route: string;
  /** Nom de la méthode pour rejoindre le groupe (ex: 'JoinDevisGroup') */
  joinMethod: string;
  /** Nom de la méthode pour quitter le groupe (ex: 'LeaveDevisGroup') */
  leaveMethod: string;
  /** Nom de l'événement à écouter (ex: 'DocumentProgress') */
  eventName: string;
  /** ID du groupe à rejoindre (ex: quoteId) */
  groupId?: string;
  /** Callback optionnel appelé à chaque notification */
  onNotification?: (notification: T) => void;
}

/**
 * Hook générique pour les notifications SignalR
 *
 * @example
 * ```tsx
 * // Pour les documents
 * const { notifications, isConnected } = useSignalRNotifications<DocumentProgressDto>({
 *   route: '/signalr/document-notifications',
 *   joinMethod: 'JoinDevisGroup',
 *   leaveMethod: 'LeaveDevisGroup',
 *   eventName: 'DocumentProgress',
 *   groupId: quoteId
 * });
 *
 * // Pour un autre type de notification
 * const { notifications } = useSignalRNotifications<MyNotificationType>({
 *   route: '/signalr/my-notifications',
 *   joinMethod: 'JoinMyGroup',
 *   leaveMethod: 'LeaveMyGroup',
 *   eventName: 'MyEvent',
 *   groupId: myGroupId
 * });
 * ```
 */
export const useSignalRNotifications = <T,>(
  config: SignalRNotificationsConfig<T>
) => {
  const {
    route,
    joinMethod,
    leaveMethod,
    eventName,
    groupId,
    onNotification
  } = config;

  const [notifications, setNotifications] = useState<T[]>([]);
  const [latestNotification, setLatestNotification] = useState<T | null>(null);

  const signalRConfig: SignalRConfig = {
    url: `${baseURL}${route}`
  };

  const {
    connection,
    isConnected,
    error,
    connect,
    disconnect,
    on,
    off,
    invoke
  } = useSignalR(signalRConfig);

  const handleNotification = useCallback(
    (notification: T) => {
      console.log(`[SignalR][${eventName}] Notification received:`, notification);
      setLatestNotification(notification);
      setNotifications(prev => [...prev, notification]);
      onNotification?.(notification);
    },
    [onNotification, eventName]
  );

  const clearNotifications = useCallback(() => {
    setNotifications([]);
    setLatestNotification(null);
  }, []);

  useEffect(() => {
    if (isConnected && groupId) {
      console.log(`[SignalR][${eventName}] Connected — joining group ${groupId}`);
      invoke(joinMethod, groupId);
      on(eventName, handleNotification);
    }

    return () => {
      if (groupId) {
        console.log(`[SignalR][${eventName}] Leaving group ${groupId}`);
        invoke(leaveMethod, groupId);
      }
      off(eventName, handleNotification);
    };
  }, [
    isConnected,
    on,
    off,
    handleNotification,
    invoke,
    groupId,
    joinMethod,
    leaveMethod,
    eventName
  ]);

  const memoizedConnect = useCallback(() => {
    connect();
  }, [connect]);

  const memoizedDisconnect = useCallback(() => {
    disconnect();
  }, [disconnect]);

  return {
    // Connection state
    connection,
    isConnected,
    error,

    // Connection methods
    memoizedConnect,
    memoizedDisconnect,

    // Notification data
    notifications,
    latestNotification,

    // Utility methods
    clearNotifications
  };
};

// ============================================
// Configurations prédéfinies pour les cas courants
// ============================================

/**
 * Configuration pour les notifications de documents
 */
export const DOCUMENT_NOTIFICATIONS_CONFIG = {
  route: '/signalr/document-notifications',
  joinMethod: 'JoinDevisGroup',
  leaveMethod: 'LeaveDevisGroup',
  eventName: 'DocumentProgress'
} as const;

/**
 * Configuration pour les notifications de documents d'un contrat collectif
 * (parcours `ContratDeGroupe`, hub partagé).
 */
export const GROUP_CONTRACT_NOTIFICATIONS_CONFIG = {
  route: '/signalr/document-notifications',
  joinMethod: 'JoinGroupContractGroup',
  leaveMethod: 'LeaveGroupContractGroup',
  eventName: 'DocumentProgress'
} as const;
